import { ApiError, apiRequest } from '../../../src/services/api';
import { getSettings } from '../../../src/services/settings';
import { uiLanguage } from '../../../src/services/language';
import type { CalendarEventsResult, CalendarStatus, GoogleCalendarDTO } from '../../../src/shared/types';
import { calendarAccounts } from '../calendarAccounts';

/**
 * `googleCalendar.ts`'nin web karşılığı: etkinlikler ve takvim listesi
 * sunucudan (web/app/api/v1/calendar/*). Sunucu masaüstüyle aynı biçimde
 * döndürüyor — Google ve Outlook birleşik, aynı hata kodlarıyla.
 */

export const PRIMARY_CALENDAR_ID = 'primary';

export async function getCalendarStatus(): Promise<CalendarStatus> {
  const { google } = await calendarAccounts();
  return { connected: google.connected && !google.expired, profile: google.profile };
}

export async function listGoogleCalendars(): Promise<GoogleCalendarDTO[]> {
  return apiRequest<GoogleCalendarDTO[]>('GET', '/api/v1/calendar/google/calendars');
}

export async function setGoogleCalendars(ids: unknown): Promise<GoogleCalendarDTO[]> {
  return apiRequest<GoogleCalendarDTO[]>('POST', '/api/v1/calendar/google/calendars', { body: { ids } });
}

export async function listCalendarEvents(fromISO: string, toISO: string): Promise<CalendarEventsResult> {
  const params = new URLSearchParams({ from: fromISO, to: toISO, lang: uiLanguage() });
  try {
    const { events, error } = await apiRequest<{ events?: CalendarEventsResult; error?: 'not_connected' | 'auth_expired' | 'network' }>(
      'GET',
      `/api/v1/calendar/events?${params.toString()}`
    );
    if (error) return { error };
    return Array.isArray(events) ? events : [];
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return { error: 'auth_expired' };
    return { error: 'network' };
  }
}

export async function getUpcomingCalendarEvents(): Promise<CalendarEventsResult> {
  const { calendarLookaheadDays } = await getSettings();
  const timeMax = new Date(Date.now() + calendarLookaheadDays * 24 * 60 * 60 * 1000);
  return listCalendarEvents(new Date().toISOString(), timeMax.toISOString());
}

export async function getTodayCalendarEvents(): Promise<CalendarEventsResult> {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return listCalendarEvents(start.toISOString(), end.toISOString());
}
